<template>
  <div class="w-full my-6 justify-between bg-white px-8 py-2 space-y-5">
    <div class="border border-grayLight text-primaryDarkBg p-6 rounded-lg">
      <div class="flex justify-between mb-4">
        <h2 class="text-xl font-bold">Learn how you invested</h2>
        <div class="flex items-center gap-2">
          <p class="bg-[#F6F8F9] text-grayDark text-[14px] font-medium rounded-[6px] px-2 py-1">{{ selectedYear }}</p>
          <div class="flex gap-2 items-center cursor-pointer" @click="toggleYearDropdown">
            <p class="text-primaryDarkBg text-[12px]">Toggle year</p>
            <img src="@/assets/projects/chevron-down.svg" alt="Dropdown Icon" />
          </div>
          <div v-if="showYearDropdown" class="absolute mt-8 right-12 bg-white shadow-lg rounded-md z-10">
            <div v-for="year in availableYears" :key="year" 
                 class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                 @click="selectYear(year)">
              {{ year }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div v-if="loading" class="flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
        </div>
        <div v-else-if="investmentData && investmentData.length > 0" class="overflow-x-auto rounded-lg">
          <table class="min-w-full border border-grayLight rounded-lg">
            <thead class="rounded-lg">
              <tr class="border-b border-grayLight rounded-lg">
                <th class="py-3 px-4 text-left font-semibold">Month</th>
                <th class="py-3 px-4 text-right font-semibold">Investment Amount</th>
                <th class="py-3 px-4 text-right font-semibold">Growth</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, index) in investmentData" :key="index" 
                  :class="{ 'border-t border-grayLight': index > 0 }">
                <td class="py-3 px-4">{{ getMonthName(data.month) }}</td>
                <td class="py-3 px-4 text-right">
                  <span class="py-1 px-2 bg-[#292D32] text-primaryLight rounded">
                    {{ formatCurrency(data.total_investment) }}
                  </span>
                </td>
                <td class="py-3 px-4 text-right">
                  <span :class="getGrowthClass(data.growth)">
                    {{ formatGrowth(data.growth) }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="border-t border-grayLight">
                <td class="py-3 px-4 font-bold">Total</td>
                <td class="py-3 px-4 text-right font-bold">
                  <span class="py-1 px-2 bg-[#292D32] text-primaryLight rounded">
                    {{ formatCurrency(getTotalInvestment()) }}
                  </span>
                </td>
                <td class="py-3 px-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else
          class="w-full border border-grayLight rounded-lg py-10 px-6 flex justify-center items-center">
          <p class="text-center">You haven't invested in any projects in {{ selectedYear }}.</p>
        </div>

        <!-- Monthly Investment Chart -->
        <div v-if="investmentData && investmentData.length > 0" class="mt-8">
          <h3 class="text-lg font-semibold mb-4">Monthly Investment Trend</h3>
          <div class="h-64 w-full">
            <!-- Replace with your preferred charting library -->
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import Chart from 'chart.js/auto';

export default {
  name: 'InvestmentSummary',
  setup() {
    const loading = ref(true);
    const investmentData = ref([]);
    const selectedYear = ref(new Date().getFullYear());
    const showYearDropdown = ref(false);
    const availableYears = ref([2025, 2024, 2023]);
    const chartInstance = ref(null);
    const chartCanvas = ref(null);

    const fetchInvestmentData = async (year) => {
      loading.value = true;
      try {
        const response = await axios.get(`/api/investors/investment-year-summary/${year}`);
        if (response.data.status_code === 200) {
          // Add growth data (you'd need to calculate this properly based on your requirements)
          investmentData.value = response.data.data.map((item, index, array) => {
            let growth = 0;
            if (index > 0 && array[index - 1].total_investment > 0) {
              growth = ((item.total_investment - array[index - 1].total_investment) / array[index - 1].total_investment) * 100;
            }
            return { ...item, growth };
          });
        } else {
          investmentData.value = [];
        }
      } catch (error) {
        console.error('Error fetching investment data:', error);
        investmentData.value = [];
      } finally {
        loading.value = false;
        // Initialize chart after data is loaded
        updateChart();
      }
    };

    const toggleYearDropdown = () => {
      showYearDropdown.value = !showYearDropdown.value;
    };

    const selectYear = (year) => {
      selectedYear.value = year;
      showYearDropdown.value = false;
      fetchInvestmentData(year);
    };

    const getMonthName = (monthNum) => {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return months[monthNum - 1];
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0);
    };

    const formatGrowth = (growth) => {
      if (!growth) return '0%';
      return `${growth > 0 ? '+' : ''}${growth.toFixed(1)}%`;
    };

    const getGrowthClass = (growth) => {
      if (!growth) return 'text-gray-500';
      return growth >= 0 ? 'text-green-500' : 'text-red-500';
    };

    const getTotalInvestment = () => {
      return investmentData.value.reduce((total, data) => total + data.total_investment, 0);
    };

    const updateChart = () => {
      if (chartInstance.value) {
        chartInstance.value.destroy();
      }
      
      if (!chartCanvas.value || !investmentData.value.length) return;

      const ctx = chartCanvas.value.getContext('2d');
      const months = investmentData.value.map(data => getMonthName(data.month));
      const investments = investmentData.value.map(data => data.total_investment);

      chartInstance.value = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: months,
          datasets: [{
            label: 'Investment Amount',
            data: investments,
            backgroundColor: '#292D32',
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return formatCurrency(value).split('.')[0];
                }
              }
            }
          }
        }
      });
    };

    onMounted(() => {
      fetchInvestmentData(selectedYear.value);
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (showYearDropdown.value && !e.target.closest('.year-dropdown')) {
          showYearDropdown.value = false;
        }
      });
    });

    watch(investmentData, () => {
      updateChart();
    });

    return {
      loading,
      investmentData,
      selectedYear,
      showYearDropdown,
      availableYears,
      chartCanvas,
      toggleYearDropdown,
      selectYear,
      getMonthName,
      formatCurrency,
      formatGrowth,
      getGrowthClass,
      getTotalInvestment
    };
  }
};
</script>